import { fiats, paymentMethods, providers } from '@/widgets/WithdrawToCard'
import {
  addBreadcrumbsBeforeApiRequest,
  captureApiError,
} from '@/shared/lib/captureApiError'
import { appApiService } from '@/shared/services/app-api.service'

function initState() {
  return {
    settings: [],
    withdraw: [],
    rate: {},
  }
}

const state = initState()

const mutations = {
  SET_SETTINGS(state, payload) {
    state.settings = payload
  },
  SET_WITHDRAW(state, payload) {
    state.withdraw = payload
  },
  SET_RATE(state, payload) {
    state.rate = payload
  },
}

const actions = {
  async FIAT_SETTINGS({ commit }) {
    try {
      // Get data from API
      const response = await this.$apiService().get('fiat-proxy/settings')

      // Filter settings with unknown parameters
      const filteredSettings = []

      response.settings.forEach((setting) => {
        if (!fiats[setting.fiat]) return
        if (!paymentMethods[setting.payment_type]) return
        if (!providers[setting.provider]) return
        filteredSettings.push(setting)
      })

      // Save filtered data to the store
      commit('SET_SETTINGS', filteredSettings)
      return filteredSettings
    } catch (error) {
      console.error('FIAT_SETTINGS', error)
      captureApiError('FIAT_SETTINGS', 'fiat-proxy/settings', error)
      return error?.response?.data
    }
  },

  // Authenticate user in Mercuryo
  async FIAT_AUTH() {
    try {
      await this.$apiService().get('fiat-proxy/auth')
      return { error: null }
    } catch (error) {
      console.error('FIAT_AUTH', { ...error })
      captureApiError('FIAT_AUTH', 'fiat-proxy/auth', error)
      return { error: error?.response?.data }
    }
  },

  async FIAT_GET_CARDS() {
    try {
      const response = await this.$apiService().get('fiat-proxy/cards')
      return { cards: response.cards, error: null }
    } catch (error) {
      console.error('FIAT_GET_CARDS', { ...error })
      captureApiError('FIAT_GET_CARDS', 'fiat-proxy/cards', error)
      return { cards: [], error: error?.response?.data }
    }
  },

  // Check if external KYC is needed and get a redirect URL for that
  async FIAT_EXTERNAL_KYC() {
    try {
      const response = await this.$apiService().get('fiat-proxy/kyc')
      return {
        hasKyc: response.has_kyc,
        redirectUrl: response.redirect_url,
        error: null,
      }
    } catch (error) {
      console.error('FIAT_EXTERNAL_KYC', { ...error })
      captureApiError('FIAT_EXTERNAL_KYC', 'fiat-proxy/kyc', error)
      return { hasKyc: null, redirectUrl: null, error: error?.response?.data }
    }
  },

  // Get redirect URL for Mercuryo when user choose a card to withdraw
  async FIAT_BIND_CARD(_, { cardId }) {
    try {
      const payload = { card_id: cardId }
      addBreadcrumbsBeforeApiRequest(
        'FIAT_BIND_CARD',
        'fiat-proxy/bind-card',
        payload
      )
      const response = await this.$apiService().post(
        'fiat-proxy/bind-card',
        payload
      )
      return {
        redirectUrl: response.redirect_url,
        merchantTransactionId: response.merchant_transaction_id,
        error: null,
      }
    } catch (error) {
      console.error('FIAT_BIND_CARD', { ...error })
      captureApiError('FIAT_BIND_CARD', 'fiat-proxy/bind-card', error)
      return {
        redirectUrl: null,
        merchantTransactionId: null,
        error: error?.response?.data,
      }
    }
  },

  async FIAT_WITHDRAW({ commit }, payload) {
    try {
      addBreadcrumbsBeforeApiRequest(
        'FIAT_WITHDRAW',
        'fiat-proxy/withdraw',
        payload
      )
      const response = await this.$apiService().post(
        'fiat-proxy/withdraw',
        payload
      )
      commit('SET_WITHDRAW', response)
      return { success: true, response }
    } catch (error) {
      console.error('FIAT_WITHDRAW', { ...error })
      captureApiError('FIAT_WITHDRAW', 'fiat-proxy/withdraw', error)
      return { success: false, response: error?.response?.data }
    }
  },

  async FIAT_RATE({ commit }, payload) {
    try {
      addBreadcrumbsBeforeApiRequest('FIAT_RATE', 'fiat-proxy/rate', payload)
      if (!payload) return null
      const response = await this.$apiService().post('fiat-proxy/rate', payload)
      commit('SET_RATE', response)
      return response
    } catch (error) {
      console.error('FIAT_RATE', { ...error })
      captureApiError('FIAT_RATE', 'fiat-proxy/rate', error)
      return error?.response?.data
    }
  },

  // Execute withdraw using paybis/withdraw endpoint
  async PAYBIS_WITHDRAW(_, { amount, coin_id, network_id, receiver_address }) {
    try {
      const payload = {
        amount,
        coin_id,
        network_id,
        receiver_address,
      }

      addBreadcrumbsBeforeApiRequest(
        'PAYBIS_WITHDRAW',
        'paybis/withdraw',
        payload
      )

      const response = await appApiService.client.post(
        'paybis/withdraw',
        payload,
        {
          headers: appApiService._headers,
        }
      )

      return {
        success: true,
        response: response.data,
        errorCode: null,
      }
    } catch (error) {
      console.error('PAYBIS_WITHDRAW', { ...error })
      captureApiError('PAYBIS_WITHDRAW', 'paybis/withdraw', error)
      return {
        success: false,
        response: null,
        errorCode:
          error.response?.data?.code ||
          error.response?.data?.message ||
          error.response?.data,
      }
    }
  },

  // Use method to validate withdraw to wallet parameters before to try to execute transaction
  async VALIDATE_WITHDRAW_TO_WALLET(
    { rootState, rootGetters },
    { address, amount, coin_id, fee, network_id, method_id }
  ) {
    try {
      if (method_id === 'instant') {
        await this.$apiService().emcdApi.post(
          `/v4/wallets/validate_withdraw${rootGetters['auth/isSub'] ? `/${rootState.auth.accountID}` : ''}`,
          {
            address,
            amount,
            coin_id,
            fee,
            network_id,
          }
        )
      }

      if (method_id === 'free') {
        await this.$apiService().emcdApi.post(
          `/v4/wallets/validate_withdraw_free${rootGetters['auth/isSub'] ? `/${rootState.auth.accountID}` : ''}`,
          {
            address,
            amount,
            coin_id,
            network_id,
          }
        )
      }

      return {
        isWithdrawValid: true,
        errorCode: null,
      }
    } catch (error) {
      console.error(
        `VALIDATE_WITHDRAW_TO_WALLET, method_id: ${method_id}`,
        error
      )
      captureApiError(
        'VALIDATE_WITHDRAW_TO_WALLET',
        '/v4/wallets/validate_withdraw_free',
        error
      )
      return {
        isWithdrawValid: false,
        errorCode:
          error.response?.data?.code ||
          error.response?.data?.message ||
          error.response?.data,
      }
    }
  },

  // Return fee for instant (paid) withdrawal or null
  async GET_WITHDRAWAL_TO_WALLET_FEE(
    _,
    { coin_id, amount, network_id, to_address }
  ) {
    try {
      const data = await this.$apiService().endpointApi.post(
        '/coin/withdrawal_fee',
        {
          coin_id,
          amount,
          network_id,
          to_address,
        }
      )
      return { fee: data.fee, validTill: data.valid_till }
    } catch (error) {
      console.error('GET_WITHDRAWAL_TO_WALLET_FEE:', { ...error })
      captureApiError(
        'GET_WITHDRAWAL_TO_WALLET_FEE',
        '/coin/withdrawal_fee',
        error
      )
      return null
    }
  },

  // Execute withdraw from wallet to wallet
  async WITHDRAW_TO_WALLET(
    { rootState, rootGetters },
    { coin_id, amount, address, network_id, fee, memo, method_id }
  ) {
    const payload = {
      coin_id,
      amount,
      address,
      network_id,
    }

    if (fee) payload.fee = fee
    if (memo) payload.comment = memo

    try {
      if (method_id === 'instant') {
        await this.$apiService().emcdApi.post(
          `v4/wallets/withdraw${
            rootGetters['auth/isSub'] ? `/${rootState.auth.accountID}` : ''
          }`,
          payload
        )
      }

      if (method_id === 'free') {
        await this.$apiService().emcdApi.post(
          `v4/wallets/withdraw_free${
            rootGetters['auth/isSub'] ? `/${rootState.auth.accountID}` : ''
          }`,
          payload
        )
      }

      return {
        success: true,
        errorCode: null,
      }
    } catch (error) {
      console.error('WITHDRAW_FROM_WALLET:', { ...error, method_id })
      captureApiError('WITHDRAW_FROM_WALLET', 'v4/wallets/withdraw', error)
      return {
        success: false,
        errorCode:
          error.response?.data?.code ||
          error.response?.data?.message ||
          error.response?.data,
      }
    }
  },
}

const getters = {
  findCoinLimits: (state) => (crypto, fiat, payment) =>
    /**
     * @param {string} crypto
     * @param {string} fiat
     * @param {string} payment
     */

    state.settings.find(
      (setting) =>
        setting.crypto === crypto &&
        setting.fiat === fiat &&
        (payment === undefined || setting.payment_type === payment)
    ),
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
}
